import moment from 'moment'
import {
  Band,
  BrandLabel,
  BrandRole,
  CitationHealth,
  CitationType,
  ContextTagType,
  OutcomeSeverity,
} from 'src/services/aiListeningApi/brandProfile.types'

// Empty or not-yet-computed values print as a dash, never NaN or "null%".
export const DASH = '—'

const isNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const formatNumber = (value: number | null | undefined): string =>
  isNumber(value) ? Math.round(value).toLocaleString('en-US') : DASH

export const formatPercent = (
  value: number | null | undefined,
  digits = 0
): string => (isNumber(value) ? `${value.toFixed(digits)}%` : DASH)

// Small rates keep a decimal ("2.7%"); rounding them to a whole number
// reads as a bigger change than it is.
export const formatRate = (value: number | null | undefined): string =>
  isNumber(value) ? formatPercent(value, value < 10 && value % 1 ? 1 : 0) : DASH

// 2,400,000 → "2.4M"
export const formatCompact = (value: number | null | undefined): string =>
  isNumber(value)
    ? new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 1,
        notation: 'compact',
      }).format(value)
    : DASH

export const formatPosition = (value: number | null | undefined): string =>
  isNumber(value) ? `#${value.toFixed(1)}` : DASH

// "+3", "-0.4" — a signed number for a delta badge label.
export const formatSigned = (value: number, digits = 0): string => {
  const rounded = Number(value.toFixed(digits))
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(digits)}`
}

export const formatDateTime = (date?: string | null): string =>
  date ? moment(date).format('MMMM D, HH:mm') : DASH

export const formatShortDate = (date?: string | null): string =>
  date ? moment(date).format('MMM D') : ''

// "https://www.silknet.com/en?utm_source=openai" → "silknet.com/en". Query
// and fragment are noise in a label (AI answers tag links with utm_source);
// the link itself keeps them.
export const displayUrl = (url?: string | null): string =>
  (url ?? '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[?#].*$/, '')

export const firstLetter = (name?: string | null): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?'

// Band of a 0–100 score (the spec's 70 / 50 thresholds), for values that
// arrive without one.
export const bandOf = (value: number | null | undefined): Band | null => {
  if (!isNumber(value)) return null
  if (value >= 70) return 'strong'
  if (value >= 50) return 'moderate'
  return 'attention'
}

export const BAND_LABELS: Record<Band, string> = {
  attention: 'Attention',
  moderate: 'Moderate',
  strong: 'Strong',
}

export const BAND_LEGEND: { band: Band; label: string }[] = [
  { band: 'strong', label: '70+ strong' },
  { band: 'moderate', label: '50–69 moderate' },
  { band: 'attention', label: 'Under 50 needs attention' },
]

export const CONTEXT_TAG_TYPES: ContextTagType[] = [
  'topic',
  'intent',
  'attribute',
  'location',
]

export const CONTEXT_TAG_LABELS: Record<ContextTagType, string> = {
  attribute: 'Attribute',
  intent: 'Intent',
  location: 'Location',
  topic: 'Topic',
}

export const CITATION_TYPE_LABELS: Record<CitationType, string> = {
  competitor: 'Competitor',
  owned: 'Owned',
  third_party: 'Third-party',
}

export const CITATION_HEALTH_LABELS: Record<CitationHealth, string> = {
  low: 'Low citations',
  moderate: 'Moderate',
  strong: 'Strong',
}

export const BRAND_LABELS: Record<BrandLabel, string> = {
  ai_discovered: 'AI-Discovered',
  tracked: 'Tracked',
  you: 'You',
}

export const ROLE_LABELS: Record<BrandRole | 'not_mentioned', string> = {
  alternative: 'Recommended alternative',
  compared: 'Compared',
  neutral: 'Neutral mention',
  not_mentioned: 'Not mentioned',
  primary: 'Primary recommendation',
}

export const SEVERITY_LABELS: Record<OutcomeSeverity, string> = {
  at_risk: 'At risk',
  critical: 'Critical',
  narrow: 'Narrow',
  strong: 'Strong',
}
